import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconBox } from '../components/Icons.jsx';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    services: [],
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        services: checked 
          ? [...prev.services, value]
          : prev.services.filter(service => service !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

      const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
          // Prepare form data
          const emailData = {
            name: formData.name,
            email: formData.email,
            company: formData.company || 'Not provided',
            services: formData.services.length > 0 ? formData.services.join(', ') : 'Not specified',
            budget: formData.budget || 'Not specified',
            message: formData.message,
            timestamp: new Date().toISOString()
          };

          // Send to Firebase Cloud Function
          const response = await fetch('https://us-central1-the-hosting-hive.cloudfunctions.net/sendContactEmail', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(emailData)
          });

          if (response.ok) {
            setSubmitStatus('success');
            // Reset form
            setFormData({
              name: '',
              email: '',
              company: '',
              services: [],
              budget: '',
              message: ''
            });
          } else {
            throw new Error('Email sending failed');
          }
        } catch (error) {
          console.error('Form submission error:', error);
          setSubmitStatus('error');
        } finally {
          setIsSubmitting(false);
        }
      };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-eyebrow">Contact</p>
          <h1 className="hero-title">Let's Discuss Your Project</h1>
          <p className="hero-subtitle">Tell us about your goals and we'll respond within one business day with recommendations and next steps.</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="services-section">
        <div className="services-container">
          <div className="contact-content">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">Ready to get started? Have questions about our services? We're here to help.</p>
            
            <div className="contact-grid">
              <div className="contact-info">
                <h3>Contact Information</h3>
                <div className="contact-item">
                  <IconBox name="email" />
                  <div className="contact-details">
                    <h4>Email</h4>
                    <p><a href="mailto:requests@thehostinghive.net">requests@thehostinghive.net</a></p>
                    <p><a href="mailto:hello@thehostinghive.net">hello@thehostinghive.net</a></p>
                    <p><small>We respond within one business day</small></p>
                  </div>
                </div>

                <div className="contact-item">
                  <IconBox name="phone" />
                  <div className="contact-details">
                    <h4>Phone</h4>
                    <p>Available by appointment</p>
                    <p><small>Contact us to schedule a call</small></p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">💼</div>
                  <div className="contact-details">
                    <h4>Business Hours</h4>
                    <p>Monday - Friday: 9 AM - 6 PM PST</p>
                    <p><small>Weekend support by appointment</small></p>
                  </div>
                </div>
              </div>
              
              <div className="contact-form">
                <h3>Send Us a Message</h3>
                <p className="form-description">Tell us about your project and we'll get back to you within 4-8 hours.</p>
                
                    <form className="contact-form-fields" id="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name" 
                        value={formData.name}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input 
                      type="text" 
                      id="company" 
                      name="company" 
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name (optional)" 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Services Interested In</label>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="services" 
                          value="custom-web-apps"
                          checked={formData.services.includes('custom-web-apps')}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        Custom Web Applications
                      </label>
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="services" 
                          value="website-development"
                          checked={formData.services.includes('website-development')}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        Website Development
                      </label>
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="services" 
                          value="managed-hosting"
                          checked={formData.services.includes('managed-hosting')}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        Managed Hosting
                      </label>
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="services" 
                          value="api-integrations"
                          checked={formData.services.includes('api-integrations')}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        API & Integrations
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="budget">Budget Range</label>
                    <select 
                      id="budget" 
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                    >
                      <option value="">Select budget range (optional)</option>
                      <option value="under-5k">Under $5,000</option>
                      <option value="5k-15k">$5,000 - $15,000</option>
                      <option value="15k-50k">$15,000 - $50,000</option>
                      <option value="over-50k">Over $50,000</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Project Details *</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows="5" 
                      value={formData.message}
                      onChange={handleInputChange}
                      required 
                      placeholder="Tell us about your project, timeline, and any specific requirements..."
                    ></textarea>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="form-success">
                      <p>Thank you! Your message has been sent successfully. We'll get back to you within 4-8 hours.</p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="form-error">
                      <p>Sorry, there was an error. Please email us directly at hello@thehostinghive.net</p>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="cta-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Quick answers to common questions</p>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How quickly can you start my project?</h4>
              <p>WordPress hosting projects can be started within 24-48 hours. Custom development projects typically begin within 1-2 weeks after planning and approval.</p>
            </div>
            
            <div className="faq-item">
              <h4>Do you offer free consultations?</h4>
              <p>Yes! We offer free 30-minute consultations to discuss your project requirements and provide initial recommendations.</p>
            </div>
            
            <div className="faq-item">
              <h4>What's your response time?</h4>
              <p>We respond to emails within 4-8 hours during business days. Phone calls are available by appointment.</p>
            </div>
            
            <div className="faq-item">
              <h4>Can you help with existing websites?</h4>
              <p>Yes! We provide migration services, optimization, and maintenance for existing websites and applications.</p>
            </div>
            
            <div className="faq-item">
              <h4>Do you work with small businesses?</h4>
              <p>Absolutely! We specialize in working with small businesses and provide personalized attention to every client.</p>
            </div>
            
            <div className="faq-item">
              <h4>What should I include in my message?</h4>
              <p>Please include your project goals, timeline, budget range, and any specific requirements. The more details you provide, the better we can assist you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Don't wait - let's discuss your project today and get you on the path to success.</p>
          <Link to="/contact#contact-form" className="cta-button">Start Your Project</Link>
        </div>
      </section>
    </div>
  );
}

export default Contact;
